import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { LogOut, User, Mail, Phone, MapPin, Calendar, TrendingUp, Wallet, Shield, ShieldCheck, RefreshCw, Settings, FolderOpen, Clock, Crown, Sparkles, FileText, Send, X, Loader2, BookOpen, Search, CheckCircle, Star, ChevronRight, DollarSign, BarChart3, Award, Lock, UserCog, ChevronDown, Lightbulb, Target, Database, Leaf, Bot, PieChart, FlaskConical, GraduationCap, MessageCircle, Upload, Download, Home, CreditCard, FileCheck, Menu, Pencil, Save, Hash, ArrowUpRight, ArrowDownLeft, Ban } from 'lucide-react';
import VerificationModal from '../components/VerificationModal';
import BackgroundMusic from '../components/BackgroundMusic';
import ChatBox from '../components/ChatBox';
import axios from 'axios';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

const LOYALTY_LEVELS = [
  { level: 1, xp: 0, name: 'Bronze', color: '#CD7F32', gradient: 'from-[#CD7F32] to-[#A0622E]' },
  { level: 2, xp: 1400, name: 'Silver', color: '#A8A9AD', gradient: 'from-[#A8A9AD] to-[#808080]' },
  { level: 3, xp: 3000, name: 'Gold', color: '#FFD700', gradient: 'from-[#FFD700] to-[#DAA520]' },
  { level: 4, xp: 6000, name: 'Platinum', color: '#7B8D8E', gradient: 'from-[#A3B5B6] to-[#7B8D8E]' },
  { level: 5, xp: 9000, name: 'Emerald', color: '#50C878', gradient: 'from-[#50C878] to-[#2E8B57]' },
  { level: 6, xp: 18000, name: 'Crimson', color: '#DC143C', gradient: 'from-[#DC143C] to-[#8B0000]' },
  { level: 7, xp: 36500, name: 'Sapphire', color: '#0F52BA', gradient: 'from-[#0F52BA] to-[#082567]' },
  { level: 8, xp: 73000, name: 'Diamond', color: '#B9F2FF', gradient: 'from-[#B9F2FF] to-[#7EC8E3]' },
  { level: 9, xp: 100000, name: 'Elite', color: '#1a1a2e', gradient: 'from-[#1a1a2e] to-[#0f0f1a]' }
];

const getNextLevel = (currentLevel) => LOYALTY_LEVELS.find(l => l.level === currentLevel + 1);

// ─── Dropdown Menu Component ────────────────────────────────────────
const NavDropdown = ({ label, icon: Icon, items, activeSection, setActiveSection, isOpen, onToggle }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onToggle(null); };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onToggle]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => onToggle(isOpen ? null : label)}
        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
          items.some(i => i.id === activeSection)
            ? 'bg-white text-[#0d6571] shadow-sm'
            : 'text-white/80 hover:text-white hover:bg-white/10'
        }`}
        data-testid={`nav-dropdown-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[220px] z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          {items.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveSection(item.id); onToggle(null); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                activeSection === item.id
                  ? 'bg-[#0d6571]/5 text-[#0d6571] font-semibold'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              data-testid={`nav-item-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
              {item.badge && <span className="ml-auto bg-[#d97841] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{item.badge}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [userInfo, setUserInfo] = useState(user);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Navigation
  const [activeSection, setActiveSection] = useState('overview');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Premium states
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [paymentChecking, setPaymentChecking] = useState(false);

  // AI Insight states
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [insightMessages, setInsightMessages] = useState([]);
  const [insightInput, setInsightInput] = useState('');
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightSessionId] = useState(crypto.randomUUID());
  const [insight, setInsight] = useState(null);
  const [insightFetching, setInsightFetching] = useState(true);

  // Report states
  const [showReport, setShowReport] = useState(false);
  const [reportContent, setReportContent] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  // Discover Projects states
  const [showDiscover, setShowDiscover] = useState(false);
  const [catalogProjects, setCatalogProjects] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [appliedIds, setAppliedIds] = useState(new Set());

  // AI Decision Journal states
  const [showJournal, setShowJournal] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalInput, setJournalInput] = useState('');
  const [journalLoading, setJournalLoading] = useState(false);
  const [journalFetching, setJournalFetching] = useState(false);

  // AI Market Insight
  const [marketInsight, setMarketInsight] = useState(null);

  // Market Data
  const [marketData, setMarketData] = useState({ crypto: [], forex: [], commodities: [], indices: [] });
  const [marketTab, setMarketTab] = useState('crypto');
  const [marketLoading, setMarketLoading] = useState(false);

  // Settings
  const [showChangeDetails, setShowChangeDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ field: '', currentValue: '', newValue: '', reason: '' });
  const [detailsSubmitting, setDetailsSubmitting] = useState(false);
  const [detailsSubmitted, setDetailsSubmitted] = useState(false);

  // Profile Edit
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Wallet
  const [walletTab, setWalletTab] = useState('overview');
  const [depositAmount, setDepositAmount] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', iban: '', accountName: '' });
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [walletTxs, setWalletTxs] = useState([]);
  const [walletTxsLoading, setWalletTxsLoading] = useState(false);

  // Certificates
  const [certificates, setCertificates] = useState([]);

  // KYC
  const [kycDocs, setKycDocs] = useState([]);
  const [kycUploading, setKycUploading] = useState(false);

  // FXC Mentor
  const [mentorMessages, setMentorMessages] = useState([]);
  const [mentorInput, setMentorInput] = useState('');
  const [mentorLoading, setMentorLoading] = useState(false);
  const [mentorSessionId] = useState(crypto.randomUUID());

  // ─── Data Fetching ─────────────────────────────────────────────────
  const refreshUserInfo = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUserInfo(response.data);
    } catch (error) { console.error('Error refreshing user info:', error); }
    finally { setIsRefreshing(false); }
  }, []);

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/projects/my`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(response.data);
    } catch (error) { console.error('Error fetching projects:', error); }
    finally { setLoadingProjects(false); }
  }, []);

  const fetchInsight = useCallback(async () => {
    setInsightFetching(true);
    try {
      const response = await axios.get(`${API}/insights/random`);
      setInsight(response.data);
    } catch { console.error('Error fetching insight'); }
    finally { setInsightFetching(false); }
  }, []);

  const fetchCertificates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/education/my-certificates`, { headers: { Authorization: `Bearer ${token}` } });
      setCertificates(response.data);
    } catch { console.error('Error fetching certificates'); }
  }, []);

  const fetchKycStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/education/kyc/status`, { headers: { Authorization: `Bearer ${token}` } });
      setKycDocs(response.data);
    } catch { console.error('Error fetching KYC status'); }
  }, []);

  const fetchMarketData = useCallback(async () => {
    setMarketLoading(true);
    try {
      const [cryptoRes, forexRes, commoditiesRes, indicesRes] = await Promise.allSettled([
        axios.get(`${API}/crypto/markets`),
        axios.get(`${API}/forex/rates`),
        axios.get(`${API}/commodities/prices`),
        axios.get(`${API}/indices/prices`),
      ]);
      setMarketData({
        crypto: cryptoRes.status === 'fulfilled' ? cryptoRes.value.data.slice(0, 10) : [],
        forex: forexRes.status === 'fulfilled' ? (forexRes.value.data.rates || []).slice(0, 10) : [],
        commodities: commoditiesRes.status === 'fulfilled' ? commoditiesRes.value.data.slice(0, 10) : [],
        indices: indicesRes.status === 'fulfilled' ? indicesRes.value.data.slice(0, 10) : [],
      });
    } catch { console.error('Error fetching market data'); }
    finally { setMarketLoading(false); }
  }, []);

  // ─── Wallet Handlers (moved up to avoid hoisting issues) ─────────────
  const fetchWalletTxs = useCallback(async () => {
    setWalletTxsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wallet/transactions`, { headers: { Authorization: `Bearer ${token}` } });
      setWalletTxs(response.data);
    } catch { console.error('Error fetching wallet transactions'); }
    finally { setWalletTxsLoading(false); }
  }, []);

  useEffect(() => {
    refreshUserInfo();
    fetchProjects();
    fetchInsight();
    fetchCertificates();
    fetchKycStatus();
  }, [refreshUserInfo, fetchProjects, fetchInsight, fetchCertificates, fetchKycStatus]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const depositSessionId = searchParams.get('deposit_session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
      setSearchParams({}, { replace: true });
    }
    if (depositSessionId) {
      setActiveSection('wallet');
      setWalletTab('history');
      pollDepositStatus(depositSessionId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch market data when section becomes active
  useEffect(() => {
    if (activeSection === 'market' && marketData.crypto.length === 0 && !marketLoading) {
      fetchMarketData();
    }
    if (activeSection === 'wallet' && walletTxs.length === 0 && !walletTxsLoading) {
      fetchWalletTxs();
    }
  }, [activeSection, marketData.crypto.length, marketLoading, fetchMarketData, walletTxs.length, walletTxsLoading, fetchWalletTxs]);

  // ─── Handlers ──────────────────────────────────────────────────────
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setPaymentChecking(false); return; }
    setPaymentChecking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/premium/status/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.paymentStatus === 'paid') { setPaymentChecking(false); await refreshUserInfo(); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000); }
  };

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/premium/checkout`, { originUrl: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) { console.error('Error initiating upgrade:', error); }
    finally { setUpgradeLoading(false); }
  };

  const handleInsightSend = async () => {
    if (!insightInput.trim() || insightLoading) return;
    const userMsg = insightInput.trim();
    setInsightInput('');
    setInsightMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setInsightLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/premium/portfolio-insight`, { message: userMsg, sessionId: insightSessionId }, { headers: { Authorization: `Bearer ${token}` } });
      setInsightMessages(prev => [...prev, { role: 'assistant', text: response.data.response }]);
    } catch { setInsightMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I encountered an error.' }]); }
    finally { setInsightLoading(false); }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/premium/generate-report`, { sessionId: crypto.randomUUID() }, { headers: { Authorization: `Bearer ${token}` } });
      setReportContent(response.data.report);
      setShowReport(true);
    } catch { console.error('Error generating report'); }
    finally { setReportLoading(false); }
  };

  const openDiscoverProjects = async () => {
    setShowDiscover(true);
    setCatalogLoading(true);
    try {
      const response = await axios.get(`${API}/catalog/projects`);
      setCatalogProjects(response.data);
    } catch { console.error('Error loading catalog projects'); }
    finally { setCatalogLoading(false); }
  };

  const handleApply = async (projectId) => {
    setApplyingId(projectId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/catalog/projects/${projectId}/apply`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAppliedIds(prev => new Set([...prev, projectId]));
    } catch (err) { if (err.response?.status === 400) setAppliedIds(prev => new Set([...prev, projectId])); }
    finally { setApplyingId(null); }
  };

  const openJournal = async () => {
    setShowJournal(true);
    setJournalFetching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/premium/decision-journal`, { headers: { Authorization: `Bearer ${token}` } });
      setJournalEntries(response.data);
    } catch { console.error('Error loading journal'); }
    finally { setJournalFetching(false); }
  };

  const handleJournalSubmit = async () => {
    if (!journalInput.trim() || journalLoading) return;
    const decision = journalInput.trim();
    setJournalInput('');
    setJournalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/premium/decision-journal`, { decision, sessionId: crypto.randomUUID() }, { headers: { Authorization: `Bearer ${token}` } });
      setJournalEntries(prev => [response.data, ...prev]);
    } catch { console.error('Error creating journal entry'); }
    finally { setJournalLoading(false); }
  };

  const handleChangeDetailsSubmit = async () => {
    if (!detailsForm.field || !detailsForm.newValue.trim()) return;
    setDetailsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/account/change-request`, {
        field: detailsForm.field, currentValue: detailsForm.currentValue,
        newValue: detailsForm.newValue.trim(), reason: detailsForm.reason.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      setDetailsSubmitted(true);
    } catch { console.error('Error submitting change request'); }
    finally { setDetailsSubmitting(false); }
  };

  const handleKycUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setKycUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/education/kyc/upload`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      fetchKycStatus();
    } catch { console.error('Error uploading KYC'); }
    finally { setKycUploading(false); }
  };

  const handleMentorSend = async () => {
    if (!mentorInput.trim() || mentorLoading) return;
    const msg = mentorInput.trim();
    setMentorInput('');
    setMentorMessages(prev => [...prev, { role: 'user', text: msg }]);
    setMentorLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/premium/mentor`, { message: msg, sessionId: mentorSessionId }, { headers: { Authorization: `Bearer ${token}` } });
      setMentorMessages(prev => [...prev, { role: 'assistant', text: response.data.response }]);
    } catch { setMentorMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I encountered an error. Please try again.' }]); }
    finally { setMentorLoading(false); }
  };

  // ─── Wallet Handlers ─────────────────────────────────────────────
  const handleDeposit = async () => {
    const amt = parseFloat(depositAmount);
    if (!amt || amt < 1) return;
    setDepositLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/wallet/deposit`, { amount: amt, originUrl: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.url) window.location.href = response.data.url;
    } catch (err) { console.error('Deposit error:', err); }
    finally { setDepositLoading(false); }
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawForm.amount);
    if (!amt || amt < 1 || !withdrawForm.iban.trim() || !withdrawForm.accountName.trim()) return;
    setWithdrawLoading(true);
    setWithdrawSuccess(false);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/wallet/withdraw`, { amount: amt, iban: withdrawForm.iban.trim(), accountName: withdrawForm.accountName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setWithdrawSuccess(true);
      setWithdrawForm({ amount: '', iban: '', accountName: '' });
      fetchWalletTxs();
      setTimeout(() => setWithdrawSuccess(false), 4000);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Withdrawal failed';
      alert(msg);
    }
    finally { setWithdrawLoading(false); }
  };

  const pollDepositStatus = async (sessionId, attempts = 0) => {
    if (attempts > 10) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wallet/deposit/status/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.paymentStatus === 'paid') {
        fetchWalletTxs();
        refreshUserInfo();
      } else if (attempts < 10) {
        setTimeout(() => pollDepositStatus(sessionId, attempts + 1), 2000);
      }
    } catch { /* ignore */ }
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileSaved(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/auth/profile`, profileForm, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        setUserInfo(prev => ({ ...prev, ...response.data.user }));
        setProfileEditing(false);
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 3000);
      }
    } catch (err) { console.error('Error saving profile:', err); }
    finally { setProfileSaving(false); }
  };

  const startProfileEdit = () => {
    const genderVal = userInfo?.gender || '';
    const normalizedGender = genderVal ? genderVal.charAt(0).toUpperCase() + genderVal.slice(1).toLowerCase() : '';
    setProfileForm({
      firstName: userInfo?.firstName || '',
      lastName: userInfo?.lastName || '',
      dateOfBirth: userInfo?.dateOfBirth || '',
      gender: normalizedGender,
      mobileNumber: userInfo?.mobileNumber || '',
      livingPlace: userInfo?.livingPlace || '',
      houseNumber: userInfo?.houseNumber || '',
      zipCode: userInfo?.zipCode || '',
      additional: userInfo?.additional || '',
    });
    setProfileEditing(true);
    setProfileSaved(false);
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const isPremium = userInfo?.isPremium;
  const loyaltyXp = userInfo?.loyaltyXp || 0;
  const loyaltyLevel = userInfo?.loyaltyLevel || 1;
  const currentTier = LOYALTY_LEVELS.find(l => l.level === loyaltyLevel) || LOYALTY_LEVELS[0];
  const nextLevel = getNextLevel(loyaltyLevel);
  const currentLevelXp = currentTier.xp;
  const xpProgress = nextLevel ? Math.min(100, ((loyaltyXp - currentLevelXp) / (nextLevel.xp - currentLevelXp)) * 100) : 100;

  if (!user) return null;

  // ─── Navigation Structure ──────────────────────────────────────────
  const navItems = [
    {
      label: 'Overview', icon: Home,
      items: [
        { id: 'overview', label: 'Dashboard', icon: PieChart },
        { id: 'projects', label: 'My Projects', icon: FolderOpen },
        { id: 'activity', label: 'Recent Activity', icon: Clock },
      ]
    },
    {
      label: 'Wallet', icon: Wallet,
      items: [
        { id: 'wallet', label: 'Balance & Transactions', icon: CreditCard },
        { id: 'market', label: 'Market Data', icon: BarChart3 },
      ]
    },
    {
      label: 'Learning', icon: GraduationCap,
      items: [
        { id: 'certificates', label: 'My Certificates', icon: Award, badge: certificates.length || null },
        { id: 'mentor', label: 'FXC Mentor', icon: MessageCircle },
      ]
    },
    {
      label: 'AI Tools', icon: Bot,
      items: [
        { id: 'insight', label: 'Portfolio Advisor', icon: Sparkles },
        { id: 'report', label: 'AI Report', icon: FileText },
        { id: 'journal', label: 'Decision Journal', icon: Lightbulb },
      ]
    },
    {
      label: 'Documents', icon: FileCheck,
      items: [
        { id: 'kyc', label: 'KYC Verification', icon: Shield },
      ]
    },
    {
      label: 'Settings', icon: Settings,
      items: [
        { id: 'profile', label: 'Profile Details', icon: User },
        { id: 'change-details', label: 'Change Details', icon: UserCog },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#f0f4f5]" data-testid="dashboard-page">

      {/* ─── Top Navigation Bar ─────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-[#0d6571] to-[#0a5460] shadow-lg sticky top-0 z-40" data-testid="dashboard-nav">
        <div className="max-w-[1440px] mx-auto px-4">
          {/* Top row: Logo + User info */}
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="text-white font-bold text-lg tracking-tight">
                FXC<span className="text-[#d97841]">.</span>
              </button>
              {isPremium && (
                <span className="flex items-center gap-1 bg-gradient-to-r from-[#d97841] to-[#e8935a] text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="premium-badge">
                  <Crown className="w-3 h-3" /> PREMIUM BUNDLE
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <BackgroundMusic audioUrl="/audio/background-music.mp3" inline />
              <button
                onClick={refreshUserInfo}
                className="text-white/60 hover:text-white transition p-1.5 rounded-lg hover:bg-white/10"
                disabled={isRefreshing}
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              <div className="text-right hidden sm:block">
                <p className="text-white text-sm font-medium">{userInfo?.firstName} {userInfo?.lastName}</p>
                <p className="text-white/50 text-xs">{userInfo?.email}</p>
              </div>
              {userInfo?.isAdmin && (
                <Button onClick={() => navigate('/admin-fxcvantage/adminpanel')} size="sm" className="bg-white/10 hover:bg-white/20 text-white rounded-full text-xs h-8 px-3" data-testid="admin-panel-link">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Admin
                </Button>
              )}
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-full h-8 px-3" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Bottom row: Navigation dropdowns */}
          <div className="hidden md:flex items-center gap-1 pb-2" data-testid="nav-bar">
            {navItems.map(group => (
              <NavDropdown
                key={group.label}
                label={group.label}
                icon={group.icon}
                items={group.items}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                isOpen={openDropdown === group.label}
                onToggle={setOpenDropdown}
              />
            ))}
          </div>

          {/* Mobile menu toggle */}
          <div className="md:hidden pb-2 flex">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white/80 hover:text-white flex items-center gap-2 text-sm py-1.5">
              <Menu className="w-4 h-4" />
              <span>Menu</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0a5460] border-t border-white/10 px-4 py-3 space-y-1">
            {navItems.flatMap(g => g.items).map(item => (
              <button
                key={item.id}
                onClick={() => { setActiveSection(item.id); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition ${
                  activeSection === item.id ? 'bg-white/15 text-white font-semibold' : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon className="w-4 h-4" /> {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ─── Payment Checking Overlay ─────────────────────────────────── */}
      {paymentChecking && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 text-center shadow-2xl">
            <Loader2 className="w-10 h-10 text-[#0d6571] animate-spin mx-auto mb-4" />
            <p className="font-bold text-gray-900">Processing your payment...</p>
            <p className="text-gray-500 text-sm mt-1">This may take a moment</p>
          </div>
        </div>
      )}

      {/* ─── Main Content ──────────────────────────────────────────────── */}
      <div className="max-w-[1440px] mx-auto px-4 py-6">

        {/* ─── OVERVIEW SECTION ────────────────────────────────────────── */}
        {activeSection === 'overview' && (
          <div className="space-y-6" data-testid="overview-section">
            {/* Welcome + Quick Stats */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Welcome Card */}
              <div className="flex-1 bg-gradient-to-br from-[#0d6571] to-[#0a5460] rounded-2xl p-6 text-white">
                <h1 className="text-2xl font-bold mb-1">Welcome back, {userInfo?.firstName}</h1>
                <p className="text-white/60 text-sm mb-6">Here's your financial overview</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/50 text-xs mb-1">Balance</p>
                    <p className="text-xl font-bold" data-testid="balance-display">&euro;{(userInfo?.balance || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs mb-1">Investments</p>
                    <p className="text-xl font-bold">&euro;{(userInfo?.activeInvestments || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs mb-1">Total Gain</p>
                    <p className="text-xl font-bold text-emerald-300">+&euro;{(userInfo?.totalGain || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>

              {/* Loyalty Card */}
              <div className="lg:w-80 bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 bg-gradient-to-r ${currentTier.gradient} rounded-full flex items-center justify-center`}>
                    <Star className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{currentTier.name} Level</p>
                    <p className="text-xs text-gray-500">{loyaltyXp.toLocaleString()} XP</p>
                  </div>
                </div>
                {nextLevel && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>{currentTier.name}</span>
                      <span>{nextLevel.name}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full bg-gradient-to-r ${currentTier.gradient} rounded-full transition-all`} style={{ width: `${xpProgress}%` }} />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{(nextLevel.xp - loyaltyXp).toLocaleString()} XP to {nextLevel.name}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Bundle Upgrade Banner (non-premium only) */}
            {!isPremium && (
              <div className="bg-gradient-to-r from-[#d97841] to-[#e8935a] rounded-2xl p-6 text-white relative overflow-hidden" data-testid="bundle-upgrade-banner">
                <div className="absolute right-0 top-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5" />
                    <span className="font-bold text-sm uppercase tracking-wider">Premium Bundle</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2">&euro;49.99/month — All-Access Learning + FXC Mentor</h3>
                  <p className="text-white/80 text-sm mb-4">Unlock all 18 courses, AI Portfolio Advisor, AI Decision Journal, AI Reports, and your personal FXC Mentor for course questions.</p>
                  <div className="flex flex-wrap gap-3 mb-5">
                    {['All Courses Included', 'FXC Mentor Chat', 'Portfolio Advisor', 'AI Reports', 'Decision Journal'].map(f => (
                      <span key={f} className="bg-white/20 text-xs px-3 py-1 rounded-full font-medium">{f}</span>
                    ))}
                  </div>
                  <Button onClick={handleUpgrade} disabled={upgradeLoading} className="bg-white text-[#d97841] hover:bg-white/90 rounded-full px-8 py-2.5 font-bold shadow-lg" data-testid="upgrade-bundle-btn">
                    {upgradeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Crown className="w-4 h-4 mr-2" />}
                    Subscribe Now
                  </Button>
                </div>
              </div>
            )}

            {/* AI Market Insight */}
            <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="ai-insight-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-[#d97841]" />
                  <h3 className="font-bold text-gray-900">Daily Market Insight</h3>
                </div>
                <button onClick={fetchInsight} className="text-gray-400 hover:text-gray-600">
                  <RefreshCw className={`w-4 h-4 ${insightFetching ? 'animate-spin' : ''}`} />
                </button>
              </div>
              {insight ? (
                <div>
                  <p className="text-sm text-[#0d6571] font-semibold mb-1">{insight.title}</p>
                  <p className="text-gray-700 leading-relaxed">{insight.content}</p>
                </div>
              ) : (
                <p className="text-gray-400 text-sm">Loading insight...</p>
              )}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Learning Center', icon: BookOpen, action: () => navigate('/education'), color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
                { label: 'Market Data', icon: BarChart3, action: () => { setActiveSection('market'); fetchMarketData(); }, color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
                { label: 'Discover Projects', icon: Search, action: openDiscoverProjects, color: 'bg-purple-50 text-purple-700 hover:bg-purple-100' },
                { label: 'Verification', icon: ShieldCheck, action: () => setShowVerificationModal(true), color: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
              ].map(qa => (
                <button key={qa.label} onClick={qa.action} className={`${qa.color} rounded-xl p-4 text-left transition group`} data-testid={`quick-action-${qa.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <qa.icon className="w-5 h-5 mb-2" />
                  <p className="font-semibold text-sm">{qa.label}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ─── PROJECTS SECTION ────────────────────────────────────────── */}
        {activeSection === 'projects' && (
          <div className="space-y-6" data-testid="projects-section">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">My Projects</h2>
              <Button onClick={openDiscoverProjects} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full text-sm">
                <Search className="w-4 h-4 mr-2" /> Discover Projects
              </Button>
            </div>
            {loadingProjects ? (
              <div className="text-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : projects.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No projects yet</p>
                <Button onClick={openDiscoverProjects} className="mt-4 bg-[#0d6571] text-white rounded-full">Browse Projects</Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {projects.map(p => (
                  <div key={p.id} className="bg-white rounded-xl p-5 shadow-sm flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{p.title}</p>
                      <p className="text-sm text-gray-500 mt-1">{p.description?.substring(0, 100)}...</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(p.status)}`}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── ACTIVITY SECTION ────────────────────────────────────────── */}
        {activeSection === 'activity' && (
          <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="activity-section">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {[
                { icon: CheckCircle, text: 'Account created', time: userInfo?.createdAt ? new Date(userInfo.createdAt).toLocaleDateString() : 'N/A', color: 'text-green-500' },
                { icon: Shield, text: `Verification: ${userInfo?.verified ? 'Completed' : 'Pending'}`, time: '', color: userInfo?.verified ? 'text-green-500' : 'text-amber-500' },
                ...(isPremium ? [{ icon: Crown, text: 'Premium Bundle activated', time: userInfo?.premiumSince ? new Date(userInfo.premiumSince).toLocaleDateString() : '', color: 'text-[#d97841]' }] : []),
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <p className="text-sm text-gray-700 flex-1">{item.text}</p>
                  <p className="text-xs text-gray-400">{item.time}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── WALLET SECTION ──────────────────────────────────────────── */}
        {activeSection === 'wallet' && (
          <div className="space-y-6" data-testid="wallet-section">
            {/* Balance Card */}
            <div className="bg-gradient-to-br from-[#0d6571] to-[#0a5460] rounded-2xl p-8 text-white">
              <p className="text-white/50 text-sm mb-2">Available Balance</p>
              <p className="text-4xl font-bold mb-6" data-testid="wallet-balance">&euro;{(userInfo?.balance || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-white/50 text-xs mb-1">Active Investments</p>
                  <p className="text-lg font-bold">&euro;{(userInfo?.activeInvestments || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-white/50 text-xs mb-1">Total Gain</p>
                  <p className="text-lg font-bold text-emerald-300">+&euro;{(userInfo?.totalGain || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</p>
                </div>
                <button onClick={() => setWalletTab('deposit')} className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition" data-testid="wallet-deposit-btn">
                  <ArrowDownLeft className="w-5 h-5 text-emerald-300 mb-1" />
                  <p className="text-sm font-semibold">Deposit</p>
                </button>
                <button onClick={() => setWalletTab('withdraw')} className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition" data-testid="wallet-withdraw-btn">
                  <ArrowUpRight className="w-5 h-5 text-orange-300 mb-1" />
                  <p className="text-sm font-semibold">Withdraw</p>
                </button>
              </div>
            </div>

            {/* Wallet Tabs */}
            <div className="flex gap-2 bg-white rounded-xl p-1.5 shadow-sm">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'deposit', label: 'Deposit' },
                { key: 'withdraw', label: 'Withdraw' },
                { key: 'history', label: 'Transaction History' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setWalletTab(tab.key); if (tab.key === 'history') fetchWalletTxs(); }}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    walletTab === tab.key ? 'bg-[#0d6571] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`wallet-tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {walletTab === 'overview' && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4">Account Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-50"><span className="text-gray-500">Account Number</span><span className="font-mono font-medium">{userInfo?.accountNumber || 'N/A'}</span></div>
                  <div className="flex justify-between py-2 border-b border-gray-50"><span className="text-gray-500">Status</span><span className={`font-medium ${userInfo?.verified ? 'text-green-600' : 'text-amber-600'}`}>{userInfo?.verified ? 'Verified' : 'Unverified'}</span></div>
                  <div className="flex justify-between py-2 border-b border-gray-50"><span className="text-gray-500">Member Since</span><span className="font-medium">{userInfo?.createdAt ? new Date(userInfo.createdAt).toLocaleDateString() : 'N/A'}</span></div>
                  <div className="flex justify-between py-2 border-b border-gray-50"><span className="text-gray-500">Subscription</span><span className="font-medium">{isPremium ? 'Premium Bundle' : 'Free'}</span></div>
                </div>
              </div>
            )}

            {/* Deposit Tab */}
            {walletTab === 'deposit' && (
              <div className="bg-white rounded-2xl shadow-sm p-6 max-w-lg" data-testid="deposit-form">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                    <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Deposit Funds</h3>
                    <p className="text-sm text-gray-500">Add funds to your account via secure payment</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Amount (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">&euro;</span>
                      <input
                        type="number" min="1" step="0.01" value={depositAmount}
                        onChange={e => setDepositAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20 focus:border-[#0d6571]"
                        data-testid="deposit-amount-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {[50, 100, 250, 500, 1000].map(amt => (
                      <button key={amt} onClick={() => setDepositAmount(String(amt))} className="flex-1 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition" data-testid={`deposit-quick-${amt}`}>
                        &euro;{amt}
                      </button>
                    ))}
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-amber-700">After payment, your deposit will be reviewed and approved by an administrator. Balance will update upon approval.</p>
                  </div>
                  <Button onClick={handleDeposit} disabled={depositLoading || !depositAmount || parseFloat(depositAmount) < 1} className="w-full bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full py-2.5" data-testid="deposit-submit-btn">
                    {depositLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Proceed to Payment
                  </Button>
                </div>
              </div>
            )}

            {/* Withdraw Tab */}
            {walletTab === 'withdraw' && (
              <div className="bg-white rounded-2xl shadow-sm p-6 max-w-lg" data-testid="withdraw-form">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Withdraw Funds</h3>
                    <p className="text-sm text-gray-500">Request a withdrawal to your bank account</p>
                  </div>
                </div>
                {withdrawSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <p className="text-sm text-green-700 font-medium">Withdrawal request submitted. Awaiting admin approval.</p>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Amount (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">&euro;</span>
                      <input
                        type="number" min="1" step="0.01" value={withdrawForm.amount}
                        onChange={e => setWithdrawForm(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="0.00"
                        className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20 focus:border-[#0d6571]"
                        data-testid="withdraw-amount-input"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Available: &euro;{(userInfo?.balance || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">IBAN</label>
                    <input
                      value={withdrawForm.iban}
                      onChange={e => setWithdrawForm(prev => ({ ...prev, iban: e.target.value }))}
                      placeholder="NL00 BANK 0000 0000 00"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20 focus:border-[#0d6571] font-mono"
                      data-testid="withdraw-iban-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Account Holder Name</label>
                    <input
                      value={withdrawForm.accountName}
                      onChange={e => setWithdrawForm(prev => ({ ...prev, accountName: e.target.value }))}
                      placeholder="Full name on bank account"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20 focus:border-[#0d6571]"
                      data-testid="withdraw-name-input"
                    />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-amber-700">Withdrawal requests are reviewed by an administrator. Processing may take 1-3 business days after approval.</p>
                  </div>
                  <Button onClick={handleWithdraw} disabled={withdrawLoading || !withdrawForm.amount || parseFloat(withdrawForm.amount) < 1 || !withdrawForm.iban.trim() || !withdrawForm.accountName.trim()} className="w-full bg-[#d97841] hover:bg-[#d97841]/90 text-white rounded-full py-2.5" data-testid="withdraw-submit-btn">
                    {withdrawLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowUpRight className="w-4 h-4 mr-2" />}
                    Submit Withdrawal Request
                  </Button>
                </div>
              </div>
            )}

            {/* Transaction History Tab */}
            {walletTab === 'history' && (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="wallet-history">
                <div className="flex items-center justify-between p-6 pb-4">
                  <h3 className="font-bold text-gray-900">Transaction History</h3>
                  <Button onClick={fetchWalletTxs} disabled={walletTxsLoading} variant="outline" className="rounded-full text-xs" data-testid="refresh-txs-btn">
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${walletTxsLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
                {walletTxsLoading ? (
                  <div className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
                ) : walletTxs.length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No transactions yet</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-6 py-3 font-semibold text-gray-600">Type</th>
                        <th className="text-right px-6 py-3 font-semibold text-gray-600">Amount</th>
                        <th className="text-center px-6 py-3 font-semibold text-gray-600">Status</th>
                        <th className="text-right px-6 py-3 font-semibold text-gray-600">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletTxs.map(tx => (
                        <tr key={tx.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition" data-testid={`tx-row-${tx.id}`}>
                          <td className="px-6 py-3.5">
                            <div className="flex items-center gap-2">
                              {tx.type === 'deposit' ? (
                                <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center"><ArrowDownLeft className="w-3.5 h-3.5 text-emerald-600" /></div>
                              ) : (
                                <div className="w-7 h-7 bg-orange-100 rounded-full flex items-center justify-center"><ArrowUpRight className="w-3.5 h-3.5 text-orange-600" /></div>
                              )}
                              <span className="font-medium text-gray-900 capitalize">{tx.type}</span>
                            </div>
                          </td>
                          <td className={`text-right px-6 py-3.5 font-mono font-semibold ${tx.type === 'deposit' ? 'text-emerald-600' : 'text-orange-600'}`}>
                            {tx.type === 'deposit' ? '+' : '-'}&euro;{tx.amount?.toFixed(2)}
                          </td>
                          <td className="text-center px-6 py-3.5">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                              tx.status === 'approved' ? 'bg-green-100 text-green-700' :
                              tx.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              tx.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {tx.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                              {tx.status === 'rejected' && <Ban className="w-3 h-3" />}
                              {tx.status === 'pending_approval' && <Clock className="w-3 h-3" />}
                              {tx.status === 'pending_payment' && <Loader2 className="w-3 h-3" />}
                              {tx.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </span>
                          </td>
                          <td className="text-right px-6 py-3.5 text-gray-500">{tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── MARKET DATA SECTION ─────────────────────────────────────── */}
        {activeSection === 'market' && (
          <div className="space-y-6" data-testid="market-data-section">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Market Data</h2>
              <Button onClick={fetchMarketData} disabled={marketLoading} variant="outline" className="rounded-full text-sm" data-testid="refresh-market-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${marketLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {/* Market Tabs */}
            <div className="flex gap-2 bg-white rounded-xl p-1.5 shadow-sm" data-testid="market-tabs">
              {[
                { key: 'crypto', label: 'Crypto' },
                { key: 'forex', label: 'Forex' },
                { key: 'commodities', label: 'Commodities' },
                { key: 'indices', label: 'Indices' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setMarketTab(tab.key)}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    marketTab === tab.key
                      ? 'bg-[#0d6571] text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`market-tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {marketLoading ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                <p className="text-gray-400 text-sm mt-2">Loading market data...</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="market-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      {marketTab === 'crypto' && <><th className="text-left px-5 py-3 font-semibold text-gray-600">Name</th><th className="text-right px-5 py-3 font-semibold text-gray-600">Price (USD)</th><th className="text-right px-5 py-3 font-semibold text-gray-600">24h Change</th><th className="text-right px-5 py-3 font-semibold text-gray-600 hidden md:table-cell">Market Cap</th></>}
                      {marketTab === 'forex' && <><th className="text-left px-5 py-3 font-semibold text-gray-600">Pair</th><th className="text-left px-5 py-3 font-semibold text-gray-600">Currency</th><th className="text-right px-5 py-3 font-semibold text-gray-600">Rate</th><th className="text-right px-5 py-3 font-semibold text-gray-600">Change %</th></>}
                      {marketTab === 'commodities' && <><th className="text-left px-5 py-3 font-semibold text-gray-600">Name</th><th className="text-right px-5 py-3 font-semibold text-gray-600">Price</th><th className="text-right px-5 py-3 font-semibold text-gray-600">Change %</th><th className="text-right px-5 py-3 font-semibold text-gray-600 hidden md:table-cell">Unit</th></>}
                      {marketTab === 'indices' && <><th className="text-left px-5 py-3 font-semibold text-gray-600">Name</th><th className="text-right px-5 py-3 font-semibold text-gray-600">Price</th><th className="text-right px-5 py-3 font-semibold text-gray-600">Change %</th><th className="text-right px-5 py-3 font-semibold text-gray-600 hidden md:table-cell">Country</th></>}
                    </tr>
                  </thead>
                  <tbody>
                    {marketTab === 'crypto' && marketData.crypto.map((c, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition" data-testid={`crypto-row-${c.symbol}`}>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-400 text-xs uppercase w-10">{c.symbol}</span>
                            <span className="font-medium text-gray-900">{c.name}</span>
                          </div>
                        </td>
                        <td className="text-right px-5 py-3.5 font-mono font-medium text-gray-900">${c.current_price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className={`text-right px-5 py-3.5 font-semibold ${(c.price_change_percentage_24h_in_currency || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {(c.price_change_percentage_24h_in_currency || 0) >= 0 ? '+' : ''}{(c.price_change_percentage_24h_in_currency || 0).toFixed(2)}%
                        </td>
                        <td className="text-right px-5 py-3.5 text-gray-500 hidden md:table-cell">${(c.market_cap / 1e9).toFixed(1)}B</td>
                      </tr>
                    ))}
                    {marketTab === 'forex' && marketData.forex.map((f, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition" data-testid={`forex-row-${f.pair}`}>
                        <td className="px-5 py-3.5 font-mono font-semibold text-gray-900">{f.pair}</td>
                        <td className="px-5 py-3.5 text-gray-600">{f.name}</td>
                        <td className="text-right px-5 py-3.5 font-mono font-medium text-gray-900">{f.rate?.toFixed(4)}</td>
                        <td className={`text-right px-5 py-3.5 font-semibold ${(f.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {(f.change || 0) >= 0 ? '+' : ''}{(f.change || 0).toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                    {marketTab === 'commodities' && marketData.commodities.map((c, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition" data-testid={`commodity-row-${c.symbol}`}>
                        <td className="px-5 py-3.5">
                          <span className="font-medium text-gray-900">{c.name}</span>
                        </td>
                        <td className="text-right px-5 py-3.5 font-mono font-medium text-gray-900">${c.price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className={`text-right px-5 py-3.5 font-semibold ${(c.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {(c.change || 0) >= 0 ? '+' : ''}{(c.change || 0).toFixed(2)}%
                        </td>
                        <td className="text-right px-5 py-3.5 text-gray-500 hidden md:table-cell">/{c.unit}</td>
                      </tr>
                    ))}
                    {marketTab === 'indices' && marketData.indices.map((idx, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50 transition" data-testid={`index-row-${idx.symbol}`}>
                        <td className="px-5 py-3.5">
                          <span className="font-medium text-gray-900">{idx.name}</span>
                        </td>
                        <td className="text-right px-5 py-3.5 font-mono font-medium text-gray-900">{idx.price?.toLocaleString('en-US', { minimumFractionDigits: 1 })}</td>
                        <td className={`text-right px-5 py-3.5 font-semibold ${(idx.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {(idx.change || 0) >= 0 ? '+' : ''}{(idx.change || 0).toFixed(2)}%
                        </td>
                        <td className="text-right px-5 py-3.5 text-gray-500 hidden md:table-cell">{idx.country}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {((marketTab === 'crypto' && marketData.crypto.length === 0) ||
                  (marketTab === 'forex' && marketData.forex.length === 0) ||
                  (marketTab === 'commodities' && marketData.commodities.length === 0) ||
                  (marketTab === 'indices' && marketData.indices.length === 0)) && (
                  <div className="text-center py-12">
                    <BarChart3 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No data available. Click refresh to load.</p>
                  </div>
                )}
              </div>
            )}

            {/* Links to full pages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Crypto', path: '/crypto', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
                { label: 'Forex', path: '/forex', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
                { label: 'Commodities', path: '/commodities', color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
                { label: 'Indices', path: '/indices', color: 'bg-purple-50 text-purple-700 hover:bg-purple-100' },
              ].map(link => (
                <button key={link.label} onClick={() => navigate(link.path)} className={`${link.color} rounded-xl p-3 text-center transition text-sm font-medium`} data-testid={`market-link-${link.label.toLowerCase()}`}>
                  View Full {link.label} Page
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ─── CERTIFICATES SECTION ────────────────────────────────────── */}
        {activeSection === 'certificates' && (
          <div className="space-y-6" data-testid="certificates-section">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">My Certificates</h2>
              <Button onClick={() => navigate('/education')} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full text-sm">
                <BookOpen className="w-4 h-4 mr-2" /> Browse Courses
              </Button>
            </div>
            {certificates.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No certificates earned yet</p>
                <p className="text-gray-400 text-sm">Complete course exams to earn certificates</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {certificates.map((cert, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md transition" data-testid={`certificate-card-${cert.courseId}`}>
                    <div className="flex items-start justify-between mb-3">
                      <Award className="w-8 h-8 text-[#d97841]" />
                      <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">{cert.score}%</span>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-1">{cert.courseTitle}</h4>
                    <p className="text-xs text-gray-500 mb-3">{cert.category} &middot; {new Date(cert.earnedAt).toLocaleDateString()}</p>
                    <a href={`${API}/education/certificates/${cert.courseId}/pdf?token=${localStorage.getItem('token')}`} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 text-sm text-[#0d6571] hover:text-[#0d6571]/80 font-medium">
                      <Download className="w-3.5 h-3.5" /> Download PDF
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── AI MENTOR SECTION ───────────────────────────────────────── */}
        {activeSection === 'mentor' && (
          <div className="space-y-6" data-testid="mentor-section">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-[#0d6571] to-[#0a5460] rounded-full flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">AI Learning Mentor</h2>
                <p className="text-sm text-gray-500">Ask questions about your courses, get study tips, and exam preparation help</p>
              </div>
            </div>

            {!isPremium ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
                <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-700 font-semibold mb-2">Premium Bundle Required</p>
                <p className="text-gray-500 text-sm mb-4">The FXC Mentor is included in your Premium Bundle (&euro;49.99/month)</p>
                <Button onClick={handleUpgrade} className="bg-[#d97841] hover:bg-[#d97841]/90 text-white rounded-full" data-testid="mentor-upgrade-btn">
                  <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {mentorMessages.length === 0 && (
                    <div className="text-center py-12">
                      <GraduationCap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">Ask me anything about your courses!</p>
                      <div className="flex flex-wrap justify-center gap-2 mt-4">
                        {['Explain compound interest', 'Help me prepare for the Gold exam', 'What course should I take next?'].map(s => (
                          <button key={s} onClick={() => { setMentorInput(s); }} className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full hover:bg-gray-200 transition">{s}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {mentorMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-[#0d6571] text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    </div>
                  ))}
                  {mentorLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-2xl px-4 py-3 rounded-bl-md">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="border-t border-gray-100 p-4 flex gap-3">
                  <input
                    value={mentorInput}
                    onChange={e => setMentorInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleMentorSend()}
                    placeholder="Ask your mentor a question..."
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20 focus:border-[#0d6571]"
                    data-testid="mentor-input"
                  />
                  <Button onClick={handleMentorSend} disabled={mentorLoading || !mentorInput.trim()} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-5" data-testid="mentor-send-btn">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── AI TOOLS: PORTFOLIO ADVISOR ─────────────────────────────── */}
        {activeSection === 'insight' && (
          <div className="space-y-6" data-testid="insight-section">
            <h2 className="text-xl font-bold text-gray-900">AI Portfolio Advisor</h2>
            {!isPremium ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
                <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-700 font-semibold mb-2">Premium Bundle Required</p>
                <Button onClick={handleUpgrade} className="bg-[#d97841] hover:bg-[#d97841]/90 text-white rounded-full">
                  <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {insightMessages.length === 0 && (
                    <div className="text-center py-12">
                      <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">Ask about portfolio strategies, market trends, or investment ideas</p>
                    </div>
                  )}
                  {insightMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-[#0d6571] text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'}`}>
                        <p className="whitespace-pre-wrap">{msg.text}</p>
                      </div>
                    </div>
                  ))}
                  {insightLoading && <div className="flex justify-start"><div className="bg-gray-100 rounded-2xl px-4 py-3 rounded-bl-md"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div></div>}
                </div>
                <div className="border-t border-gray-100 p-4 flex gap-3">
                  <input value={insightInput} onChange={e => setInsightInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleInsightSend()} placeholder="Ask about your portfolio..." className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20 focus:border-[#0d6571]" data-testid="insight-input" />
                  <Button onClick={handleInsightSend} disabled={insightLoading || !insightInput.trim()} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-5" data-testid="insight-send-btn"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── AI TOOLS: REPORT ────────────────────────────────────────── */}
        {activeSection === 'report' && (
          <div className="space-y-6" data-testid="report-section">
            <h2 className="text-xl font-bold text-gray-900">AI Portfolio Report</h2>
            {!isPremium ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
                <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-700 font-semibold mb-2">Premium Bundle Required</p>
                <Button onClick={handleUpgrade} className="bg-[#d97841] hover:bg-[#d97841]/90 text-white rounded-full"><Crown className="w-4 h-4 mr-2" /> Upgrade to Premium</Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <p className="text-gray-600 text-sm mb-4">Generate a comprehensive AI-powered portfolio analysis report.</p>
                <Button onClick={handleGenerateReport} disabled={reportLoading} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full" data-testid="generate-report-btn">
                  {reportLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />} Generate Report
                </Button>
                {showReport && reportContent && (
                  <div className="mt-6 bg-gray-50 rounded-xl p-6 border border-gray-100">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{reportContent}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── AI TOOLS: DECISION JOURNAL ──────────────────────────────── */}
        {activeSection === 'journal' && (
          <div className="space-y-6" data-testid="journal-section">
            <h2 className="text-xl font-bold text-gray-900">AI Decision Journal</h2>
            {!isPremium ? (
              <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
                <Lock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-700 font-semibold mb-2">Premium Bundle Required</p>
                <Button onClick={handleUpgrade} className="bg-[#d97841] hover:bg-[#d97841]/90 text-white rounded-full"><Crown className="w-4 h-4 mr-2" /> Upgrade to Premium</Button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <p className="text-gray-600 text-sm mb-4">Log your investment decisions and get AI-powered analysis.</p>
                <div className="flex gap-3 mb-6">
                  <input value={journalInput} onChange={e => setJournalInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleJournalSubmit()} placeholder="Describe your decision..." className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20" data-testid="journal-input" />
                  <Button onClick={handleJournalSubmit} disabled={journalLoading || !journalInput.trim()} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-5" data-testid="journal-submit-btn">
                    {journalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {!showJournal ? (
                  <Button onClick={openJournal} variant="outline" className="rounded-full" data-testid="load-journal-btn"><Clock className="w-4 h-4 mr-2" /> Load Journal Entries</Button>
                ) : journalFetching ? (
                  <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
                ) : journalEntries.length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">No entries yet</p>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {journalEntries.map((entry, i) => (
                      <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-sm font-semibold text-gray-900 mb-1">{entry.decision}</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{entry.analysis}</p>
                        <p className="text-xs text-gray-400 mt-2">{entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : ''}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── KYC SECTION ─────────────────────────────────────────────── */}
        {activeSection === 'kyc' && (
          <div className="space-y-6" data-testid="kyc-section">
            <h2 className="text-xl font-bold text-gray-900">KYC Verification</h2>
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <p className="text-gray-600 text-sm mb-4">Upload your identity documents for verification. Accepted formats: JPG, PNG, PDF.</p>
              <label className={`inline-flex items-center gap-2 bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-5 py-2.5 text-sm font-medium cursor-pointer transition ${kycUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                {kycUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {kycUploading ? 'Uploading...' : 'Upload Document'}
                <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf" onChange={handleKycUpload} data-testid="kyc-upload-input" />
              </label>
            </div>
            {kycDocs.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-4">Uploaded Documents</h3>
                <div className="space-y-3">
                  {kycDocs.map((doc, i) => (
                    <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                          <p className="text-xs text-gray-400">{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : ''}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        doc.status === 'approved' ? 'bg-green-100 text-green-700' :
                        doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{doc.status?.charAt(0).toUpperCase() + doc.status?.slice(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── PROFILE SECTION ─────────────────────────────────────────── */}
        {activeSection === 'profile' && (
          <div className="space-y-6" data-testid="profile-section">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Profile Details</h2>
              {!profileEditing ? (
                <Button onClick={startProfileEdit} variant="outline" className="rounded-full text-sm" data-testid="edit-profile-btn">
                  <Pencil className="w-4 h-4 mr-2" /> Edit Profile
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={() => setProfileEditing(false)} variant="outline" className="rounded-full text-sm" data-testid="cancel-edit-btn">Cancel</Button>
                  <Button onClick={handleProfileSave} disabled={profileSaving} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full text-sm" data-testid="save-profile-btn">
                    {profileSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
                  </Button>
                </div>
              )}
            </div>

            {profileSaved && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2" data-testid="profile-saved-msg">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-700 font-medium">Profile updated successfully</p>
              </div>
            )}

            {/* Read-only fields */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Account Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'Email', value: userInfo?.email, icon: Mail },
                  { label: 'Username', value: userInfo?.username, icon: User },
                  { label: 'Account Number', value: userInfo?.accountNumber, icon: Hash },
                ].map(({ label, value, icon: Ic }) => (
                  <div key={label} className="flex items-center gap-3 py-3 px-4 bg-gray-50 rounded-xl">
                    <Ic className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="text-sm font-medium text-gray-900 truncate">{value || 'N/A'}</p>
                    </div>
                    <Lock className="w-3 h-3 text-gray-300" />
                  </div>
                ))}
              </div>
            </div>

            {/* Editable fields */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Personal Details</h3>
              {profileEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  {[
                    { key: 'firstName', label: 'First Name', icon: User, type: 'text' },
                    { key: 'lastName', label: 'Last Name', icon: User, type: 'text' },
                    { key: 'dateOfBirth', label: 'Date of Birth', icon: Calendar, type: 'text', placeholder: 'DD-MM-YYYY' },
                    { key: 'gender', label: 'Gender', icon: User, type: 'select', options: ['Male', 'Female', 'Other'] },
                    { key: 'mobileNumber', label: 'Phone Number', icon: Phone, type: 'text' },
                    { key: 'livingPlace', label: 'City', icon: MapPin, type: 'text' },
                    { key: 'houseNumber', label: 'House Number', icon: MapPin, type: 'text' },
                    { key: 'zipCode', label: 'Zip Code', icon: MapPin, type: 'text' },
                    { key: 'additional', label: 'Additional Address Info', icon: MapPin, type: 'text' },
                  ].map(({ key, label, icon: Ic, type, placeholder, options }) => (
                    <div key={key}>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
                        <Ic className="w-3.5 h-3.5 text-gray-400" />
                        {label}
                      </label>
                      {type === 'select' ? (
                        <select
                          value={profileForm[key] || ''}
                          onChange={e => setProfileForm(prev => ({ ...prev, [key]: e.target.value }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20 focus:border-[#0d6571] bg-white"
                          data-testid={`profile-edit-${key}`}
                        >
                          <option value="">Select...</option>
                          {options.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input
                          type={type}
                          value={profileForm[key] || ''}
                          onChange={e => setProfileForm(prev => ({ ...prev, [key]: e.target.value }))}
                          placeholder={placeholder || label}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20 focus:border-[#0d6571]"
                          data-testid={`profile-edit-${key}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    { label: 'First Name', value: userInfo?.firstName, icon: User },
                    { label: 'Last Name', value: userInfo?.lastName, icon: User },
                    { label: 'Date of Birth', value: userInfo?.dateOfBirth, icon: Calendar },
                    { label: 'Gender', value: userInfo?.gender, icon: User },
                    { label: 'Phone', value: userInfo?.mobileNumber, icon: Phone },
                    { label: 'City', value: userInfo?.livingPlace, icon: MapPin },
                    { label: 'House Number', value: userInfo?.houseNumber, icon: MapPin },
                    { label: 'Zip Code', value: userInfo?.zipCode, icon: MapPin },
                    { label: 'Additional', value: userInfo?.additional, icon: MapPin },
                  ].map(({ label, value, icon: Ic }) => (
                    <div key={label} className="flex items-center gap-3 py-3 border-b border-gray-50">
                      <Ic className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">{label}</p>
                        <p className="text-sm font-medium text-gray-900">{value || 'N/A'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── CHANGE DETAILS SECTION ──────────────────────────────────── */}
        {activeSection === 'change-details' && (
          <div className="space-y-6" data-testid="change-details-section">
            <h2 className="text-xl font-bold text-gray-900">Request Account Change</h2>
            <div className="bg-white rounded-2xl shadow-sm p-6 max-w-lg">
              {detailsSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="font-bold text-gray-900 mb-1">Request Submitted</p>
                  <p className="text-gray-500 text-sm">Our team will review your request.</p>
                  <Button onClick={() => { setDetailsSubmitted(false); setDetailsForm({ field: '', currentValue: '', newValue: '', reason: '' }); }} className="mt-4 rounded-full" variant="outline">Submit Another</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">What do you want to change?</label>
                    <select value={detailsForm.field} onChange={e => setDetailsForm(prev => ({ ...prev, field: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20" data-testid="change-field-select">
                      <option value="">Select field</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone Number</option>
                      <option value="address">Address</option>
                      <option value="name">Name</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">New Value</label>
                    <input value={detailsForm.newValue} onChange={e => setDetailsForm(prev => ({ ...prev, newValue: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20" placeholder="Enter new value" data-testid="change-new-value" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Reason (optional)</label>
                    <textarea value={detailsForm.reason} onChange={e => setDetailsForm(prev => ({ ...prev, reason: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20" rows={3} placeholder="Why are you requesting this change?" data-testid="change-reason" />
                  </div>
                  <Button onClick={handleChangeDetailsSubmit} disabled={detailsSubmitting || !detailsForm.field || !detailsForm.newValue.trim()} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full w-full" data-testid="change-submit-btn">
                    {detailsSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Submit Request
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── MODALS ────────────────────────────────────────────────────── */}

      {/* Discover Projects Modal */}
      {showDiscover && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Discover Projects</h3>
              <button onClick={() => setShowDiscover(false)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            {catalogLoading ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
            ) : catalogProjects.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No projects available</p>
            ) : (
              <div className="space-y-4">
                {catalogProjects.map(p => (
                  <div key={p.id} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-gray-900">{p.title}</h4>
                        <p className="text-sm text-gray-500 mt-1">{p.description?.substring(0, 150)}</p>
                      </div>
                      <Button onClick={() => handleApply(p.id)} disabled={applyingId === p.id || appliedIds.has(p.id)} size="sm" className="rounded-full shrink-0 ml-3" variant={appliedIds.has(p.id) ? 'outline' : 'default'}>
                        {appliedIds.has(p.id) ? <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Applied</> : applyingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && <VerificationModal onClose={() => setShowVerificationModal(false)} onSuccess={() => { setShowVerificationModal(false); refreshUserInfo(); }} />}

      {/* ChatBox */}
      <ChatBox />
    </div>
  );
};

export default DashboardPage;
